import {
  extendObservable,
  observable,
  action,
  reaction,
  autorun,
  computed,
} from "mobx";
import RequestStore from "./RequestStore";
import SingleRequestHandler from "./SingleRequestHandler";

/**
 * @class RequestHandler
 * @param {object} [config={}]
 * @param {string} [config.baseUrl=""]
 * @param {boolean} [config.instantParameterRefresh=true] - wenn true, wird der state eines Requests neu (auf instantParameterRefreshState) gesetzt, wenn sich ein parameterwert ändert
 * @param {string} [config.instantParameterRefreshState="toRequest"] - State, auf den gesetzt wird, wenn parameter sich ändern
 * @param {string} [config.initState="nothing"] - initialer State der Stores
 * @param {boolean} [config.onDemandRequest=false] - Setzt den State automatisch auf "toRequest" wenn der Wert ausgelesen wird, und der State dabei "nothing" ist
 * @param {boolean} [config.validTime] - Zeit, nach der automatisch der State neu gesetzt wird (auf unvalidState)
 * @param {string} [config.unvalidState="toRequest"] - state, auf den gesetzt wird, wenn die validTime abläuft. Timer wird gestartet, sobald der State success ist
 * @param {string} [config.proxyUrl] - ProxyUrl
 * @param {string} [config.method="POST"] - Abfragemethode
 * @param {string} [config.bodyType="json"] - Bodytype der Abfrage
 * @param {*} [config.responseHandling] - standardverhalten zum "auspacken" der response - String oder Funktion oder Path
 * @param {*} [config.isError] - standardverhalten zur Fehlerangabe - String oder Funktion oder Path
 * @param {*} [config.errorMessage] - standardverhalten zur Fehlermessageangabe - String oder Funktion oder Path
 * @param {string} [config.defaultBehaviour="none"] - standardverhalten allgemein (bspw. "esri")
 * @param {Array<Object>} [config.server=[]] - Array an vorkofiguroerten Servern
 * @param {string} [config.server.serverUrl] - Array an vorkofiguroerten Servern
 * @param {string} [config.server.key] -name Des servwers
 * @param {Array<Object>} [config.requests=[]] - Array an vorkonfigurierten Requests
 * @param {string} config.requests.key - key des requests (unter dem dieser erreichbar ist)
 * @param {string} [config.requests.fullUrl] - Gesamte URL des Requests
 * @param {string} [config.requests.url] - Endteil der Url des Requests (wird mit ServerUrl oder baseUrl zusammengesetzt)
 * @param {string} [config.requests.server] - server, bei dem ggf noch Konfigurationen gespeichert sind
 * @param {*} [config.requests.defaultVal] - Wert, der genutzt wird, solange kein Wert vom Server vorhanden ist
 * @param {*} [config.requests.rest] - Alle weiteren Felder, die auch auf oberster Ebene definiert sindm, werden pro Request von unten nach oben kaskadierend gesetzt. Bspw. wird die methid erst in der Requestkonfig selber nachgeschaut, dann im Server (falls vorhanden) und dann in der obersten Config (und erst zum schluss auf den Default gesetzt)
 * @param {Array<string>} [config.requests.affecting] - andere Requests, die durch diesen beeinflusst werden. Bspw "addBestand" hat vermutlich "Bestände" in der affectingliste
 * @param {boolean} [config.store] - Externer Store bspw für Parameter
 * @param {boolean} [config.historyComputionCustom] - ja, wie erklär ich das?
 */

class RequestHandler {
  constructor(config = {}) {
    const cascadeingConfigFields = [
      "instantParameterRefreshState",
      "instantParameterRefresh",
      "validTime",
      "proxyUrl",
      "method",
      "bodyType",
      "responseHandling",
      "isError",
      "errorMessage",
      "synchoModus",
      "autoRefresh",
      "affecting",
      "historyComputionCustom",
      "unvalidState",
    ];
    const {
      instantParameterRefreshState = "toRequest",
      instantParameterRefresh = true,
      validTime,
      proxyUrl,
      bodyType,
      responseHandling,
      defaultBehaviour = "none",
      synchoModus,
      autoRefresh,
      affecting,
      historyComputionCustom,
      unvalidState,
      requests = [],
      params = [],
      server = [],
      method = "POST",
      initState = "nothing",
      onDemandRequest = false,
      store,
    } = config;
    this.defaultBehaviour = defaultBehaviour;
    this.instantParameterRefreshState = instantParameterRefreshState;
    this.instantParameterRefresh = instantParameterRefresh;
    this.validTime = validTime;
    this.proxyUrl = proxyUrl;
    this.bodyType = bodyType;
    this.synchoModus = synchoModus;
    this.autoRefresh = autoRefresh;
    this.affecting = affecting;
    this.historyComputionCustom = historyComputionCustom;
    this.unvalidState = unvalidState;
    this.requests = requests;
    this.server = server;
    this.method = method;
    this.config = config;
    this.initState = initState;
    this.onDemandRequest = onDemandRequest;
    this.singleRequestHandler = {};
    this.params = params;
    RequestStore.requestHandler = this;
    RequestStore.setParameterStore(store);
    this.createServer();
    this.createRequests(); //handle checkedOutStart
  }
  createRequests() {
    const { requests = [] } = this.config;
    this.addRequests(requests);
  }

  addRequests(configs) {
    //Fange ab: festprameterkram
    configs.forEach((c) => {
      this.addRequestConf(c);
    });
  }
  addRequestConf(config) {
    RequestStore.addRequest(config.key, config);
    this.singleRequestHandler[config.key] = new SingleRequestHandler(config);
  }

  /**
   * Fügt mehrere Requests mit festem Parameter hinzu.
   * Dabei kann als parameter sowohl ein eindimensionales Array (ein parameter mit mehreren Werten) als auch ein zweidimensionales Array (eine Liste von parameter mit je Werten)
   * @param {string} key
   * @param {Array} parameter
   */
  addMultipleFixedParameterRequests(key, parameter) {
    if (Array.isArray(parameter)) {
      if (parameter.length > 0 && Array.isArray(parameter[0])) {
        parameter[0].forEach((p) => {
          const pKey = key + "/" + p;
          this.addMultipleFixedParameterRequests(
            pKey,
            parameter.filter((k, i) => i > 0)
          );
        });
      } else {
        parameter.forEach((p) => {
          this.addRequestKey(key + "/" + p);
        });
      }
      if (parameter.length === 0) {
        this.addRequestKey(key);
      }
    }
  }

  getMatchingKeys(key) {
    //keys, die im anfang matchen, bspw key = btt => auch btt/123; btt/234
    return this.config.requests
      .filter((c) => c.key.startsWith(key))
      .map((c) => c.key);
  }

  addRequestsKey(keys) {
    keys.forEach((key) => this.addRequestKey(key));
  }
  addRequestKey(key) {
    if (!RequestStore.requests[key]) {
      if (key.includes("/")) {
        // uneindeutigkeiten? zwei slash?
        const findConfig = (configs, key) => {
          const fixedParameterValues = key.split("/");
          const firstKey = fixedParameterValues.splice(0, 1);
          const fitConfigs = configs.filter((c) => {
            if (c.key.startsWith(firstKey[0])) {
              if (c.key.split("/").length == fixedParameterValues.length + 1) {
                return true;
              }
            }
            return false;
          });
          const fitConfig =
            fitConfigs.length == 0
              ? {}
              : JSON.parse(JSON.stringify(fitConfigs[0]));
          const fixedParameterKeys = fitConfig.key.split("/").map((k, i) => {
            return k.substr(1, k.length - 2);
          }); //TODO
          fixedParameterKeys.splice(0, 1);
          return {
            fixedParameterKeys,
            fitConfig,
            fixedParameterValues,
            firstKey,
          };
        };
        const { fixedParameterKeys, fitConfig, fixedParameterValues } =
          findConfig(this.config.requests, key);

        fixedParameterKeys.forEach((k, i) => {
          fitConfig.fixedParameter[k] = fixedParameterValues[i];
        });
        fitConfig.key = key;
        RequestStore.addRequest(key, fitConfig);
        this.singleRequestHandler[key] = new SingleRequestHandler(fitConfig);
      } else {
        const fitConfig = this.config.requests.filter((r) => r.key == key)[0];
        RequestStore.addRequest(key, fitConfig);
        this.singleRequestHandler[key] = new SingleRequestHandler(fitConfig);
      }
      // suche passende config
      // beachte "addBTT/123" z.b.
      // was ist mit post parametern?
    }
  }
  createServer() {
    this.server = {};
    const { server = [] } = this.config;
    server.forEach((s) => {
      this.server[s.name] = s; //new Service(s)
    });
  }
  checkOut() {}

  request(key) {
    if (!Array.isArray(key)) {
      key = [key];
    }
    key.forEach((k) => {
      this.addRequestKey(k);
      RequestStore.requests[k].setState("toRequest");
    });
  }

  upload() {
    this.singleRequestHandler
      .map((rh) => rh.getStoredRequests()) //gibt je ein array zurück, in dem schon alle nötigen configoptionen eingefügt sind => arra von arrays
      .flat() // flattened das array zu einem array flacher requestsconfigs
      .sort((c1, c2) => c1.timestamp - c2.timestamp) // sortiere nach timestamp
      .reduce(
        // promise chaining und upload
        (promiseChain, currentConfig) => {
          return promiseChain.then(Request.handleRequest(currentConfig)); //jaja, hier fehlt noch ein .catch()...
        },
        new Promise((resolve) => {
          resolve();
        }) //initial promise
      );
  }
}
export default RequestHandler;
